import { useEffect, useState } from "react";
import { Field, Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import classNames from "classnames";
import { format, isValid, parse, parseISO } from "date-fns";
import style from "../EventsAudit/EventAuditList.module.css";

import { useAppSelector } from "../../shared/hook";
import Pagination from "../../shared/components/Table/Pagination";
import { EmptyListPlaceholderContent } from "../../shared/Ui/EmptyPageBlock";
import CalendarSelectFormik from "../../shared/Ui/Calendar/CalendarSelectFormik";
import SelectCustomFormik from "../../shared/Ui/SelectCustom/SelectCustomFormik";
import ActionDropwdownCell from "../../shared/components/Table/ActionDropwdownCell";
import StatusBadge from "../../shared/Ui/StatusBadge/StatusBadge";
import ModalComponent from "../../shared/Ui/ModalComponent/ModalWindow";
import ModalButton from "../../shared/Ui/Buttons/ModalButtons";
import {
  exportReport,
  exportReportPdf,
  ReportJobStatus,
  ReportListItem,
  searchReports,
} from "../../common/api/billingApi";
import { downloadPdfBlob, downloadXlsxBlob } from "./billingUtils";

type PaginationState = {
  pageIndex: number;
  pageSize: number;
};

type BalanceReportFilters = {
  dateFrom: string;
  dateTo: string;
  number: string;
  status: ReportJobStatus | "";
};

const emptyFilters: BalanceReportFilters = {
  dateFrom: "",
  dateTo: "",
  number: "",
  status: "",
};

const toApiDate = (value: string) => {
  if (!value) return undefined;
  const parsed = parse(value, "dd.MM.yyyy", new Date());
  return isValid(parsed) ? format(parsed, "yyyy-MM-dd") : undefined;
};

const formatDisplayDateTime = (value?: string) => {
  if (!value) return "-";
  const parsed = parseISO(value);
  if (!isValid(parsed)) return value;
  return format(parsed, "dd.MM.yyyy HH:mm");
};

// Тип плашки — как в журнале отчётов «Статус КМ»
const getStatusMeta = (status: string, t: (key: string) => string) => {
  switch (status) {
    case "GENERATING":
      return { type: "test", label: t("billing.reportStatusGenerating") };
    case "READY":
    case "GENERATED":
      return { type: "active", label: t("billing.reportStatusGenerated") };
    case "ERROR":
      return { type: "error", label: t("billing.reportStatusError") };
    default:
      return { type: "", label: status || "-" };
  }
};

function BalanceReportPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pageSize = useAppSelector((state) => state.userSettings.pageSize);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize,
  });
  const [tableData, setTableData] = useState<{
    totalItems: number;
    totalPages: number;
  }>({ totalItems: 0, totalPages: 0 });
  const [values, setValues] = useState<ReportListItem[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [activeTooltipId, setActiveTooltipId] = useState<number | null>(null);
  const [filters, setFilters] = useState<BalanceReportFilters>(emptyFilters);
  const [downloadModalValue, setDownloadModalValue] = useState<ReportListItem | null>(null);

  const calculateItemNumber = (index: number) =>
    pagination.pageIndex * pagination.pageSize + index + 1;

  const statusOptions = [
    { value: "READY", label: t("billing.reportStatusGenerated") },
    { value: "GENERATING", label: t("billing.reportStatusGenerating") },
    { value: "ERROR", label: t("billing.reportStatusError") },
  ];

  const loadList = async (page = pagination.pageIndex + 1) => {
    try {
      const response = await searchReports(page, pagination.pageSize, {
        number: filters.number || undefined,
        status: filters.status || undefined,
        dateFrom: toApiDate(filters.dateFrom),
        dateTo: toApiDate(filters.dateTo),
      });
      const items = response?.reports ?? [];

      setValues(items);
      setTableData({
        totalItems: response?.totalItems ?? items.length,
        totalPages: response?.totalPages ?? 0,
      });
    } catch (error) {
      console.error("Error fetching balance reports list:", error);
      setValues([]);
      setTableData({ totalItems: 0, totalPages: 0 });
    }
  };

  useEffect(() => {
    loadList();
  }, [pagination, filters]);

  const reportFileName = (value: ReportListItem) => value.number ?? `№${value.id}`;

  const handleDownloadExcel = async (value: ReportListItem) => {
    if (!value.id) return;
    try {
      const blob = await exportReport(value.id);
      downloadXlsxBlob(blob, `Балансовый отчет ${reportFileName(value)}.xlsx`);
    } catch (error) {
      console.error("Error exporting balance report:", error);
      window.alert(t("billing.exportError"));
    } finally {
      setDownloadModalValue(null);
    }
  };

  const handleDownloadPdf = async (value: ReportListItem) => {
    if (!value.id) return;
    try {
      const blob = await exportReportPdf(value.id);
      downloadPdfBlob(blob, `Балансовый отчет ${reportFileName(value)}.pdf`);
    } catch (error) {
      console.error("Error exporting balance report to PDF:", error);
      window.alert(t("billing.exportError"));
    } finally {
      setDownloadModalValue(null);
    }
  };

  const buildActions = (value: ReportListItem) => {
    const actions: { title: string; action: string | (() => void) }[] = [
      { title: t("billing.view"), action: `/billing/balance-report/${value.id}` },
    ];
    if (value.status === "READY" || value.status === "GENERATED") {
      actions.push({
        title: t("mainButtons.download"),
        action: () => setDownloadModalValue(value),
      });
    }
    return actions;
  };

  const applyFilter = (next: BalanceReportFilters) => {
    setFilters({ ...next, number: next.number.trim() });
    setIsFilterOpen(false);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const renderFilterInputs = () => (
    <Formik initialValues={filters} enableReinitialize onSubmit={applyFilter}>
      {(formik) => (
        <Form className="min-w-[320px] p-2">
          <div className="flex flex-row justify-between items-center mb-2">
            <p className="text-lg font-semibold">
              {t("documents_upload.filters")}
            </p>
            <button type="button" onClick={() => setIsFilterOpen(false)}>
              <img src="/icons/close.svg" alt="" />
            </button>
          </div>
          <div className={style.inputField}>
            <label className={style.inputLabel}>{t("billing.generatedAt")}</label>
            <div className="grid grid-cols-2 gap-2">
              <CalendarSelectFormik
                name="dateFrom"
                documentStatus="new"
                outputFormat="dd.MM.yyyy"
                placeholder={t("participants_registry_report.date_from_ph")}
              />
              <CalendarSelectFormik
                name="dateTo"
                documentStatus="new"
                outputFormat="dd.MM.yyyy"
                placeholder={t("participants_registry_report.date_to_ph")}
              />
            </div>
          </div>
          <div className={style.inputField}>
            <label className={style.inputLabel}>{t("billing.reportNumber")}</label>
            <Field
              type="text"
              name="number"
              className={style.placeholder}
              placeholder={t("billing.reportNumberPlaceholder")}
            />
          </div>
          <div className={style.inputField}>
            <label className={style.inputLabel}>{t("billing.status")}</label>
            <Field
              component={SelectCustomFormik}
              name="status"
              isClearable
              isMulti={false}
              size="dm"
              options={statusOptions}
              placeholder={t("documentLabels.choose")}
              onChange={(next: string) => formik.setFieldValue("status", next || "")}
            />
          </div>
          <div className="flex flex-row gap-2 mt-3">
            <button
              type="submit"
              className="bg-green text-white font-semibold py-2 px-4 rounded"
            >
              {t("mainButtons3.apply")}
            </button>
            <button
              type="button"
              className="bg-bg text-gray-500 font-semibold py-2 px-4 rounded"
              onClick={() => applyFilter(emptyFilters)}
            >
              {t("mainButtons3.reset")}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );

  return (
    <div className="flex flex-1 flex-col min-w-0">
      <h2 className="my-3 text-xl font-semibold">
        {t("billing.balanceReportTitle")}
      </h2>
      <div className="bg-white my-5 p-5 pt-8 relative rounded-xl shadow-md flex flex-row items-center justify-between">
        <button
          type="button"
          className="bg-green text-white font-semibold py-2 px-4 rounded"
          onClick={() => navigate("/billing/balance-report/new")}
        >
          {t("billing.createNewReport")}
        </button>
        <div className="flex flex-row gap-[10px] items-center">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsFilterOpen(true);
            }}
            className="bg-green text-white font-semibold py-2 px-4 rounded"
            data-tooltip-offset={4}
            data-tooltip-id="billing-balance-report-filter"
          >
            {t("mainButtons3.filter")}
          </button>
          <button type="button" onClick={() => loadList()}>
            <img src="/icons/update.svg" alt="update" />
          </button>
        </div>
      </div>
      <div className="w-full">
        <div className="rounded-lg overflow-x-auto bg-white">
          <table className="max-w-full w-full leading-normal">
            <thead className="border-b-2 border-gray-300">
              <tr>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  №
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  {t("billing.reportNumber")}
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  {t("billing.generatedAt")}
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  {t("billing.executor")}
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  {t("billing.status")}
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200" />
              </tr>
            </thead>
            {values.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan={6} className="text-center py-5">
                    <EmptyListPlaceholderContent title={t("billing.noData")} />
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody className="font-normal text-center">
                {values.map((value, index) => {
                  const st = getStatusMeta(value.status, t);
                  return (
                    <tr
                      key={`balance-report-${value.id ?? index}`}
                      className={value.id ? "cursor-pointer hover:bg-gray-100" : ""}
                      onClick={() => {
                        if (value.id) {
                          navigate(`/billing/balance-report/${value.id}`);
                        }
                      }}
                    >
                      <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                        {calculateItemNumber(index)}
                      </td>
                      <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                        {value.number ?? value.id ?? "-"}
                      </td>
                      <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                        {formatDisplayDateTime(value.createdAt)}
                      </td>
                      <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                        {value.executorFio ?? "-"}
                      </td>
                      <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                        <div className="flex justify-center">
                          <StatusBadge type={st.type}>{st.label}</StatusBadge>
                        </div>
                      </td>
                      <td
                        className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {value.id && (
                          <ActionDropwdownCell
                            id={value.id}
                            actions={buildActions(value)}
                            activeTooltipId={activeTooltipId}
                            onTooltipToggle={(id) =>
                              setActiveTooltipId((prev) => (prev === id ? null : id))
                            }
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>
        <Pagination
          totalItems={tableData.totalItems}
          totalPages={tableData.totalPages}
          pagination={pagination}
          setPagination={setPagination}
        />
      </div>
      <div className="z-[1000] relative">
        <Tooltip
          isOpen={isFilterOpen}
          id="billing-balance-report-filter"
          className={classNames(style.wrapperTooltip, "drop-shadow-2xl")}
          openOnClick
          clickable
          noArrow
          opacity={1}
          place="bottom-end"
          afterShow={() => setIsFilterOpen(true)}
          afterHide={() => setIsFilterOpen(false)}
        >
          <div onClick={(e) => e.stopPropagation()}>{renderFilterInputs()}</div>
        </Tooltip>
      </div>
      <ModalComponent
        isOpen={Boolean(downloadModalValue)}
        onClose={() => setDownloadModalValue(null)}
        headerContent={t("orderList2.downloadFile")}
        content={
          downloadModalValue && (
            <div className="flex flex-col space-y-2">
              <button
                type="button"
                className="bg-green text-white font-semibold py-2 px-4 rounded hover:opacity-90 transition duration-300"
                onClick={() => handleDownloadExcel(downloadModalValue)}
              >
                {t("mainButtons.download")} Excel
              </button>
              <button
                type="button"
                className="bg-blue-500 text-white font-semibold py-2 px-4 rounded hover:opacity-90 transition duration-300"
                onClick={() => handleDownloadPdf(downloadModalValue)}
              >
                {t("mainButtons.download")} PDF
              </button>
            </div>
          )
        }
        customContent={
          <ModalButton
            type="button"
            color="grey"
            onClick={() => setDownloadModalValue(null)}
          >
            {t("mainButtons.close")}
          </ModalButton>
        }
      />
    </div>
  );
}

export default BalanceReportPage;
